import 'frontend/inertia/stylesheets/global.css'

import { createInertiaApp, inertia } from '@inertiajs/svelte'
import { trackInertiaPageViews } from 'frontend/inertia/tracking'
import { trackInertiaSessionStarts } from 'frontend/inertia/convert_session_tracking'
import { announceInertiaNavigations } from 'frontend/inertia/page_announcer'
import { reportInertiaNetworkErrors } from 'frontend/inertia/network_errors'
import { leaveInertiaForOtherPages } from 'frontend/inertia/boundary'
import { initInertiaNativeAppBridge } from 'frontend/inertia/native_app'
import { startFlashBridge } from 'frontend/inertia/stores/flash'
import { watchAuthStateFlash } from 'frontend/inertia/stores/auth.svelte'
import { setLinkAction } from 'shared/javascripts/components/link_action'
import { addDeviceTypeClass } from 'shared/javascripts/util/device_class'
import { isNativeApp } from 'shared/javascripts/util/detection'
import { inTestEnvironment } from 'shared/javascripts/util/environment'

setLinkAction(inertia)

// Registered before the app mounts, so the first `navigate` isn't missed.
if (!import.meta.env.SSR) {
  if (import.meta.env.DEV) {
    // Restores CSS hot reloading. With `compilerOptions.css: 'injected'` a component appends its <style>
    // only when no element with that id exists yet — and SSR has already written one for every component
    // it rendered. Svelte's hot update removes the tags the client appended itself, which those are not,
    // so they survive every update and CSS edits never reach the page. Dropping them before hydration
    // hands ownership back to the client, which re-appends them; the page is still cloaked until
    // createInertiaApp resolves, so nothing renders unstyled in between.
    document.head.querySelectorAll('style[id^="svelte-"]').forEach((style) => style.remove())
  }

  trackInertiaPageViews()
  trackInertiaSessionStarts()
  announceInertiaNavigations()
  reportInertiaNetworkErrors()
  leaveInertiaForOtherPages()
  startFlashBridge()
  watchAuthStateFlash()

  // Safe before hydration: Svelte doesn't own <body>.
  addDeviceTypeClass()

  if (isNativeApp()) {
    initInertiaNativeAppBridge()
  }

  void import('frontend/shared/javascripts/boot/gdpr')
  void import('frontend/shared/javascripts/boot/convert')
  void import('frontend/shared/javascripts/modules/adblock_notification/adblock_notification')
  void import('frontend/shared/javascripts/boot/survey_banner')
}

void createInertiaApp({
  pages: {
    path: '../frontend/inertia/pages',
    // Controllers live in a `frontend` namespace (Frontend::…, Career::Frontend::…), the pages don't repeat it.
    transform: (name) => name.replace(/(^|\/)frontend\//, '$1'),
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' }
    },
  },
}).then(() => {
  if (import.meta.env.DEV) {
    document.documentElement.classList.remove('vite-cloaked')
  }

  if (!import.meta.env.SSR && inTestEnvironment()) {
    document.documentElement.dataset.inertiaHydrated = ''
  }
})
